export type IProductList = {
  errors: Array<string>;
  success: boolean;
  data: Array<IProductCategory>;
};

export type IProductCategory = {
  title: string;
  data: Array<IProduct>;
};

export type IProduct = {
  id: number;
  name: string;
  description: string;
  image: string | null;
  new_price: number;
  old_price: number;
  show_people_served_information: boolean;
  people_served: number;
  featured: boolean;
  // atenção: as propriedades abaixo vão existir apenas
  // em itens com 'new_price' igual a zero, e quando um dos grupos de composição tiver controle de sabores (flavour_control = true)
  // pois nesse caso ao invés de exibir um preço, iremos mostrar 'a partir de: R$' e o menor preço da lista de sabores
  compositionGroupList: Array<ICompositionGroupList>;
  compositionGroupItemList: Array<ICompositionGroupItemList>;
};

export type IProductFull = {
  errors: Array<string>;
  success: boolean;
  data: IProductFullData;
};

export type IProductFullData = {
  id: number;
  name: string;
  description: string;
  image: string;
  new_price: number;
  old_price: number;
  people_served: number;
  featured: boolean;
  place_id: number;
  internal_product_id: number;
  group_name: string;
  combo_kit: boolean;
  max_flavours: number;
  created_at: string;
  updated_at: string;
  category_id: number;
  subcategory_id: number;
  hits: number;
  active: boolean;
  additional_flag: boolean;
  image_resize_mode_cover: boolean;
  show_people_served_information: boolean;
  delivery_vehicle_type: string;
  delivery_quantity_condition: number;
  delivery_tax: number;
  has_delivery_modifier: boolean;
  delivery_tax_modifier_final_value: boolean;
  delivery_tax_modifier_each_km: boolean;
  delivery_tax_modifier_each_item: boolean;
  additionalList: Array<string>;
  kitItemList: Array<string>;
  subcategoryInfo: ISubCategoryInfo;
  compositionGroupList: Array<ICompositionGroupList>;
  compositionGroupItemList: Array<ICompositionGroupItemList>;
};

// se temos algum grupo que controla sabores, o produto controla sabores
// obs: se algum dia tiver 'kit' ou 'combo', talvez tenha de alterar isto, ou provavelmente manter isto
// mas criar um 'product' que pode ter um 'productlist' ou algo assim.
export function isProductFullDataFlavourControl(productData: PartialProduct | undefined): boolean {
  const compositionGroupWithFlavourControl = productData?.compositionGroupList
    ?.filter(g=>g.flavour_control)

  // se aqui undefined ou não tiver grupo-composição que controla sabores, retorna false
  if(!compositionGroupWithFlavourControl) return false;

  // só vai ser true se tiver ao menos um grupo que controla sabores
  return compositionGroupWithFlavourControl.length > 0
}

/**
 * Se o produto controla sabores e o preço é zero, deve mostrar o 'A partir de'
 * @param productData
 */
export function shouldShowStartingAt(productData: PartialProduct | undefined): boolean {
  const controlaSabores = isProductFullDataFlavourControl(productData)
  const precoZero = productData?.new_price === 0

  return controlaSabores && precoZero
}

export type PartialProduct = IProductFullData | IProduct

export function startingAtText(productData: PartialProduct | undefined): string {
  if(!shouldShowStartingAt(productData)) return ''

  return `A partir de ${minimumPriceInCurrencyForFlavourControlAndNewPriceZero(productData)}`
}

export function minimumPriceInCurrencyForFlavourControlAndNewPriceZero(productData: PartialProduct | undefined): string {
  const minimum = minimumPriceForFlavourControlAndNewPriceZero(productData)
  const minumumInCurrency = minimum?.toLocaleString("pt-br", {
    style: "currency",
    currency: "BRL",
  }) ?? "(erro)"

  return minumumInCurrency
}

export function minimumPriceForFlavourControlAndNewPriceZero(productData: PartialProduct | undefined): number | undefined {
 const idGrupoControlaSabores = productData?.compositionGroupList
   .filter(g => g.flavour_control)[0]?.id;

  if(!idGrupoControlaSabores) return undefined;

  const itemsDoGrupoQueControlaSabores = productData?.compositionGroupItemList
    .filter(i=>i.product_composition_id === idGrupoControlaSabores)

  // return the price that is the least of all
  return itemsDoGrupoQueControlaSabores
    ?.reduce((acc, g)=>g.price < acc ? g.price : acc , itemsDoGrupoQueControlaSabores[0]?.price ?? 0)
}

export function displayPriceOrStartingAt(productData: PartialProduct | undefined): string {
  if (shouldShowStartingAt(productData)) return startingAtText(productData)

  if (!productData) return ''

  return productData.new_price.toLocaleString("pt-br", {
    style: "currency",
    currency: "BRL",
  })
}

export type ISubCategoryInfo = {
  id: number;
  place_id: number;
  description: string;
  category_id: number;
  png_image_url: string;
  svg_image_url: string;
  feature_all_products: boolean;
  hits: number;
  featured: boolean;
  product_count: number;
  index: number;
  active: boolean;
};

export type ICompositionGroupList = {
  id: number;
  product_id: number;
  min: number;
  max: number;
  mandatory: boolean;
  description: string;
  friendly_description: string;
  style_quantity: boolean;
  style_selection: boolean;
  calc_type_avg: boolean;
  calc_type_max: boolean;
  active: boolean;
  promotional: boolean;
  flavour_control: boolean;
  appearence_order: number;
  creation: boolean;
  edition: boolean;
  itemsList: Array<string>;
};


export type IItemsCompositionGroups = {
  id: number;
  users_orders_id: number;
  users_order_item_id: number;
  composition_group_id: number;
  compositionGroupInfo: ICompositionGroupList;
  compositionGroupItems: Array<ICompositionGroupItemList>;
};

export type ICompositionGroup = {
  composition_group_id: number;
  id?: number;
  users_orders_id?: number;
  users_order_item_id?: number;
  compositionGroupItems: Array<ICompositionItemObject>;
  compositionGroupInfo: ICompositionGroupList;
};

export type ICompositionGroupItemList = {
  id: number;
  product_composition_id: number;
  product_id: number;
  name: string;
  description: string;
  quantity: number;
  selected: boolean;
  price: number;
  active: boolean;
  internal_product_id: string;
  creation: boolean;
  edition: boolean;
};


export type ICompositionItemObject = {
  id?: number;
  composition_item_id: number;
  name: string;
  quantity: number;
  price: number;
  total: number;
  users_orders_items_composition_groups_id: 354;
  cancelled_at?: string;
  internal_product_id?: string;
  cancellation_reason?: string;
  observations?: string;
  selected?: boolean;
};

